import React, { useState, useEffect, useRef } from "react";
import { Crown, Bot, Trophy, Zap, Lock, ArrowUp, Play, ArrowRight } from "lucide-react";
import { Radar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import "./DashboardVisual.css";

ChartJS.register(
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

/* Les graphiques Chart.js sont dessines dans un <canvas> : leurs polices sont
   posees en pixels et ignorent l'echelle fluide du CSS. Ce hook relit la
   font-size de <html> (celle du clamp) et rend le facteur d'echelle, pour
   que les libelles du radar grandissent comme le reste sur grand ecran. */
function useEchelleRacine() {
  const lire = () =>
    parseFloat(getComputedStyle(document.documentElement).fontSize) / 16 || 1;
  const [echelle, setEchelle] = useState(lire);
  useEffect(() => {
    const maj = () => setEchelle(lire());
    window.addEventListener("resize", maj);
    return () => window.removeEventListener("resize", maj);
  }, []);
  return echelle;
}

/* Arrondit une taille de police canvas a l'echelle courante. */
function pxEchelle(px, echelle) {
  return Math.round(px * echelle);
}


/* Le modele repond en Markdown quoi qu'on lui demande : « **Evolution :** » et
   « 1. **Optimise ton impact** : ... » arrivaient donc a l'ecran avec leurs
   asterisques. Plutot que de lutter contre le modele dans le prompt, on rend le
   gras. Decoupage en fragments React et non `dangerouslySetInnerHTML` : le texte
   vient d'un tiers, il n'a pas a pouvoir injecter du HTML dans la page. */
function TexteIA({ children, className, style }) {
  const texte = typeof children === "string" ? children : "";
  const fragments = texte.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <p className={className} style={{ whiteSpace: "pre-line", ...style }}>
      {fragments.map((bout, i) =>
        bout.startsWith("**") && bout.endsWith("**") && bout.length > 4
          ? <strong key={i}>{bout.slice(2, -2)}</strong>
          : bout
      )}
    </p>
  );
}

function champSplash(name) {
  return `https://ddragon.leagueoflegends.com/cdn/img/champion/splash/${name}_0.jpg`;
}

function DashboardVisual({ report, onSubscribe, onCreateAccount = null, onShare, onChallenge,
  onReroast = null, onRoastLevel = null, roastLevel = "piquant", roastLoading = false,
  trophies = null, newTrophies = [], onReset, onAccuracyFeedback, history = [], squadRanking = [], leaderboard = [], community = [], isPublic = false, onTogglePublish, onViewProfile, shareLoading = false, readOnly = false, onBack = null, t = (k) => k, lang = "fr", isDemoMode = false, currentUser = null }) {
  const isPremium = report.premium !== false;
  // Deux droits differents, longtemps confondus sous `isPremium` : l'historique et
  // la Communaute sont **cumulatifs** — la grille tarifaire les donne au compte
  // gratuit — alors que le Coach, le Squad et l'image de champion sont
  // **consommables** et restent derriere le Pass. Les verrouiller tous les deux
  // sur `isPremium` promettait en grille ce que le tableau de bord refusait.
  const estConnecte = !!currentUser;

  // Traduit une réponse d'erreur du coach IA en message utile.
  // Sans ça toute erreur — y compris un 403 « réservé aux Premium » — s'affichait
  // en « Service IA indisponible », ce qui masque l'upsell et rend les pannes indiagnosticables.
  const aiErrorMessage = (status) => {
    if (status === 403) {
      return lang === "fr"
        ? "Le Coach IA est réservé aux membres Premium."
        : "The AI Coach is reserved for Premium members.";
    }
    if (status === 429) {
      return lang === "fr"
        ? "Trop de demandes, réessaie dans quelques minutes."
        : "Too many requests, try again in a few minutes.";
    }
    return lang === "fr"
      ? "Service IA indisponible, réessaie plus tard."
      : "AI service unavailable, try again later.";
  };

  // Un visiteur non connecté doit d'abord créer un compte : le premier Pass 24h
  // est offert à l'inscription, il n'a donc rien à débloquer avant d'avoir un compte.
  const handleUpsell = () => {
    if (!currentUser && onCreateAccount) return onCreateAccount();
    onSubscribe && onSubscribe();
  };
  const [tipsExpanded, setTipsExpanded] = React.useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [periode, setPeriode] = useState("tout");

  const [displayScore, setDisplayScore] = useState(0);
  const [arcScore, setArcScore] = useState(0);
  const animKeyRef = useRef(null);

  useEffect(() => {
    const key = `${report.player}_${report.score}`;
    if (animKeyRef.current === key) return;
    animKeyRef.current = key;
    setDisplayScore(0);
    setArcScore(0);

    const target = report.score;
    // Phase 1 : rapide, chiffres aléatoires (0–100)
    // Phase 2 : moyen, chiffres proches du vrai score (±20)
    // Phase 3 : lent, chiffres très proches (±5)
    const phases = [
      { count: 16, interval: 65,  range: () => Math.floor(Math.random() * 101) },
      { count: 8,  interval: 130, range: () => Math.floor(Math.max(0, Math.min(100, target + (Math.random() * 40 - 20)))) },
      { count: 4,  interval: 220, range: () => Math.floor(Math.max(0, Math.min(100, target + (Math.random() * 10 - 5)))) },
    ];

    const timers = [];
    let delay = 0;
    phases.forEach(({ count, interval, range }) => {
      for (let i = 0; i < count; i++) {
        const t = setTimeout(() => setDisplayScore(range()), delay);
        timers.push(t);
        delay += interval;
      }
    });
    const reveal = setTimeout(() => {
      setDisplayScore(target);
      setTimeout(() => setArcScore(target), 50);
    }, delay);
    timers.push(reveal);

    return () => timers.forEach(clearTimeout);
  }, [report.player, report.score]);
  const accuracyKey = `rms_accuracy_${report.player}_${report.region || ""}`;
  const [accuracyVoted, setAccuracyVoted] = useState(() => !!localStorage.getItem(accuracyKey));
  const [accuracyError, setAccuracyError] = useState(false);
  const [aiRoast, setAiRoast] = useState(null);
  const [aiRoastLoading, setAiRoastLoading] = useState(false);
  const [aiRoastError, setAiRoastError] = useState(null);
  const [followupQ, setFollowupQ] = useState("");
  const [followupA, setFollowupA] = useState(null);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [followupError, setFollowupError] = useState(null);
  const HISTORY_PREVIEW = 3;
  const clamp100 = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return Math.max(0, Math.min(100, Math.round(num)));
  };

  // Les rapports partagés générés avant la refonte du score ont encore les anciennes
  // clés en base : on retombe dessus pour ne pas afficher quatre zéros sur un lien
  // que quelqu'un a partagé il y a des mois.
  const axes = [
    { key: "combat", label: t("axis_combat"), value: clamp100(report.stats?.Combat ?? report.stats?.Aim ?? 0) },
    { key: "macro", label: t("axis_macro"), value: clamp100(report.stats?.Macro ?? report.stats?.GameSense ?? 0) },
    { key: "survival", label: t("axis_survival"), value: clamp100(report.stats?.Survival ?? report.stats?.Clutch ?? 0) },
    { key: "impact", label: t("axis_impact"), value: clamp100(report.stats?.Impact ?? 0) },
  ];
  const mainRole = report.scoreDetail?.mainRole || report.extra?.mainRole || "";

  // Champion principal joué → image de la carte badge (≥3 parties en priorité)
  const topChamp = (() => {
    const perf = report.mapPerformance;
    if (!perf?.length) return "Jinx";
    const reliable = perf.filter(cp => (cp.games ?? 1) >= 3);
    return (reliable.length > 0 ? reliable[0] : perf[0]).map;
  })();

  // Champion secondaire → carte déco
  const secondChamp =
    report.mapPerformance?.length > 1
      ? report.mapPerformance[1].map
      : topChamp;

  // Image badge = top champion joué par le joueur
  const badgeChamp = topChamp;

  useEffect(() => {
    [champSplash(badgeChamp), champSplash(secondChamp)].forEach(src => {
      const img = new window.Image();
      img.src = src;
    });
  }, [badgeChamp, secondChamp]);

  const mapPerformance =
    report.mapPerformance?.length
      ? report.mapPerformance.slice(0, 8)
      : [{ map: "—", score: 0 }];

  const radarData = {
    labels: mapPerformance.map((m) => m.map),
    datasets: [
      {
        label: "Performance par champion",
        data: mapPerformance.map((m) => m.score),
        backgroundColor: "rgba(91, 212, 255, 0.28)",
        borderColor: "#5bd4ff",
        borderWidth: 2,
        pointBackgroundColor: "#fff",
        pointBorderColor: "#5bd4ff",
      },
    ],
  };

  const echelle = useEchelleRacine();
  const isLight = document.body.getAttribute("data-mode") === "light";
  const options = {
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          color: isLight ? "#4a6080" : "#d6e6ff",
          stepSize: 20,
          backdropColor: "transparent",
          font: { size: pxEchelle(12, echelle) },
        },
        grid: { color: isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.12)", circular: false },
        angleLines: { color: isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.4)" },
        pointLabels: { color: isLight ? "#1a2332" : "#ffffff", font: { size: pxEchelle(13, echelle), weight: "700" } },
      },
    },
    plugins: { legend: { display: false } },
  };

  const friendsRanking = squadRanking.map((r) => ({
    name: r.player,
    score: r.score,
    badge: r.badge,
    isCurrentPlayer: r.player === report.player,
  }));

  const playerHistory = [...history]
    .filter((item) => item.player === report.player)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const accentColor = getComputedStyle(document.documentElement).getPropertyValue("--gold").trim() || "#c89b3c";

  // Avant/apres : c'est la preuve du progres, pas le score brut. On compare au
  // plus ancien rapport qui porte des metriques — ceux d'avant le 2026-08-30
  // n'en ont pas, d'ou le filtre.
  const progression = (() => {
    const avecMetriques = playerHistory.filter(
      (i) => i.deaths != null || i.kda != null || i.winrate != null
    );
    if (avecMetriques.length < 2) return null;
    const debut = avecMetriques[0];
    const fin = avecMetriques[avecMetriques.length - 1];
    const jours = Math.max(
      1,
      Math.round((new Date(fin.created_at) - new Date(debut.created_at)) / 86400000)
    );
    const lignes = [
      { cle: "score",   label: t("prog_score"),   avant: debut.score,   apres: fin.score,   mieux: "haut" },
      { cle: "deaths",  label: t("prog_deaths"),  avant: debut.deaths,  apres: fin.deaths,  mieux: "bas"  },
      { cle: "kda",     label: t("prog_kda"),     avant: debut.kda,     apres: fin.kda,     mieux: "haut" },
      { cle: "winrate", label: t("prog_winrate"), avant: debut.winrate, apres: fin.winrate, mieux: "haut" },
    ].filter((l) => l.avant != null && l.apres != null && l.avant !== l.apres);
    return lignes.length ? { jours, lignes } : null;
  })();

  // ─── Courbe de progression : periode et agregation ───
  // Un joueur actif accumule un rapport par session : sur six mois la courbe
  // devenait un mur de points, avec la meme date affichee plusieurs fois en
  // abscisse. On decoupe donc en deux temps : filtrer la periode demandee,
  // puis regrouper les rapports par intervalle tant qu'il en reste trop.

  const PERIODES = [
    { cle: "semaine", jours: 7,   libelle: "periode_semaine" },
    { cle: "mois",    jours: 30,  libelle: "periode_mois" },
    { cle: "annee",   jours: 365, libelle: "periode_annee" },
    { cle: "tout",    jours: null, libelle: "periode_tout" },
  ];

  const depuis = (jours) =>
    jours == null ? null : new Date(Date.now() - jours * 86400000);

  const filtrerPeriode = (entrees, jours) => {
    const borne = depuis(jours);
    return borne ? entrees.filter((i) => new Date(i.created_at) >= borne) : entrees;
  };

  // Une periode qui ne contient pas de quoi tracer une ligne est proposee mais
  // desactivee : mieux vaut un bouton grise qu'un graphique vide au clic.
  const periodesDispo = PERIODES.map((p) => ({
    ...p,
    points: filtrerPeriode(playerHistory, p.jours).length,
  }));

  const periodeActive = periodesDispo.find((p) => p.cle === periode) || periodesDispo[3];
  const historiquePeriode = filtrerPeriode(playerHistory, periodeActive.jours);

  // Cle de regroupement par pas de temps. Le lundi sert de debut de semaine.
  const cleIntervalle = (date, pas) => {
    const d = new Date(date);
    if (pas === "mois") return `${d.getFullYear()}-${d.getMonth()}`;
    if (pas === "semaine") {
      const lundi = new Date(d);
      lundi.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      lundi.setHours(0, 0, 0, 0);
      return `s${lundi.getTime()}`;
    }
    return d.toDateString();
  };

  const regrouper = (entrees, pas) => {
    const paquets = new Map();
    entrees.forEach((item) => {
      const cle = cleIntervalle(item.created_at, pas);
      if (!paquets.has(cle)) paquets.set(cle, []);
      paquets.get(cle).push(item);
    });
    return [...paquets.values()].map((groupe) => ({
      date: new Date(groupe[0].created_at),
      score: Math.round(groupe.reduce((t, i) => t + i.score, 0) / groupe.length),
      nombre: groupe.length,
      pas,
    }));
  };

  // On garde le pas le plus fin qui tienne sous le seuil : le detail n'est
  // sacrifie que quand il cesse d'etre lisible.
  const MAX_POINTS = 30;
  const MIN_POINTS = 6;
  const serie = (() => {
    const brut = historiquePeriode.map((i) => ({
      date: new Date(i.created_at), score: i.score, nombre: 1, pas: "brut",
    }));
    if (brut.length <= MAX_POINTS) return brut;
    let precedent = brut;
    for (const pas of ["jour", "semaine", "mois"]) {
      const groupe = regrouper(historiquePeriode, pas);
      if (groupe.length <= MAX_POINTS) {
        // Passer au pas superieur pour quelques points de trop ecraserait la
        // courbe : 25 rapports sur 25 jours tombaient a 5 points hebdomadaires.
        // Tant que le pas plus fin reste tracable, il vaut mieux que le grossier.
        if (groupe.length < MIN_POINTS && precedent.length <= MAX_POINTS * 1.6) return precedent;
        return groupe;
      }
      precedent = groupe;
    }
    return precedent;
  })();

  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const etiquette = (point) => {
    if (point.pas === "mois") return point.date.toLocaleDateString(locale, { month: "short", year: "2-digit" });
    return point.date.toLocaleDateString(locale, { day: "numeric", month: "short" });
  };

  const avgScore = serie.length >= 2
    ? Math.round(serie.reduce((s, p) => s + p.score, 0) / serie.length)
    : null;

  // Au-dela d'une quinzaine de points, les pastilles se touchent : on les
  // efface et on laisse la ligne lissee porter la lecture.
  const enVagues = serie.length > 15;

  const lineChartData = serie.length >= 2 ? {
    labels: serie.map(etiquette),
    datasets: [
      {
        data: serie.map((p) => p.score),
        borderColor: accentColor,
        backgroundColor: accentColor + "22",
        borderWidth: 2,
        pointBackgroundColor: accentColor,
        pointRadius: enVagues ? 0 : 4,
        pointHoverRadius: 5,
        tension: enVagues ? 0.45 : 0.3,
        fill: true,
      },
      {
        data: serie.map(() => avgScore),
        borderColor: accentColor + "55",
        borderWidth: 1,
        borderDash: [4, 4],
        pointRadius: 0,
        fill: false,
      },
    ],
  } : null;

  // La liste suit la periode choisie, comme la courbe. Elle garde en revanche
  // tous les joueurs analyses, la ou la courbe ne trace que le joueur courant.
  const formattedHistory = filtrerPeriode(history, periodeActive.jours).map((item) => ({
    date: new Date(item.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "long" }),
    player: item.player,
    score: item.score,
    phrase: item.badge || item.phrase,
  }));

  const libelleAgregat = serie.length
    ? t(serie[0].pas === "brut" ? "agregat_brut" : `agregat_${serie[0].pas}`, historiquePeriode.length)
    : null;

  const lineChartOptions = {
    responsive: true,
    plugins: { legend: { display: false }, tooltip: {
      callbacks: { label: (ctx) => ctx.datasetIndex === 0
        ? t("chart_tooltip_score", ctx.parsed.y) + t("chart_tooltip_agregat", serie[ctx.dataIndex]?.nombre || 1)
        : t("chart_tooltip_avg", ctx.parsed.y) },
    }},
    scales: {
      y: { min: 0, max: 100, ticks: { color: isLight ? "#4a6080" : "#8fa6c2", stepSize: 25, font: { size: pxEchelle(12, echelle) } }, grid: { color: isLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.07)" } },
      // La carte est etroite en colonne : sans plafond, Chart.js colle les dates
      // les unes aux autres (« 17 aout22 aout26 aout »).
      x: { ticks: { color: isLight ? "#4a6080" : "#8fa6c2", maxRotation: 0, autoSkip: true, maxTicksLimit: 6, font: { size: pxEchelle(12, echelle) } }, grid: { display: false } },
    },
  };

  return (
    <div className={`dashboard${isPremium ? " dashboard--premium" : ""}`}>
      {readOnly && (
        <div className="readonly-banner">
          {onBack && (
            <button className="readonly-back" onClick={onBack}>{t("back")}</button>
          )}
          <span>{t("readonly_viewing")} <strong>{report.player}</strong> — <span className="readonly-score">{report.score}/100</span> — <em style={{ color: "var(--muted)", fontStyle: "italic" }}>"{report.badge}"</em></span>
          <button className="readonly-cta" onClick={() => window.open(window.location.origin, "_blank")}>
            {t("generate_yours")}
          </button>
        </div>
      )}

      <div className="dash-body">
        <div className="player-bar">
          <div className="player-id">
            <div className={`avatar${isPremium ? " avatar--premium" : ""}`}>{report.player.charAt(0).toUpperCase()}</div>
            <div>
              <div className="player-name">
                {report.player}
                {report.level > 0 && (
                  <span className="player-level">{t("niv")} {report.level}</span>
                )}
              </div>
              <div className="player-game">
                LEAGUE OF LEGENDS
                {report.rank && (() => {
                  const tier = report.rank.split(" ")[0].toLowerCase();
                  return <span className={`player-rank rank-${tier}`}>{report.rank}</span>;
                })()}
              </div>
            </div>
          </div>
          {report.extra?.trend && report.extra.trend !== "stable" && (
            <div className={`trend-badge trend-${report.extra.trend}`}>
              {report.extra.trend === "up" ? t("trend_up") : t("trend_down")}
            </div>
          )}
          <div className="score-circle">
            <svg viewBox="0 0 36 36" className="score-svg">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--gold-a20)" strokeWidth="2.5" />
              <circle
                cx="18" cy="18" r="15.9" fill="none"
                stroke={displayScore < 45 ? "#e05c5c" : "var(--gold)"}
                strokeWidth="2.5"
                strokeDasharray={`${(arcScore / 100) * 100} 100`}
                strokeLinecap="round"
                transform="rotate(-90 18 18)"
                style={{ transition: "stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1)" }}
              />
            </svg>
            <div className="score-inner">
              <span className="score-number">{displayScore}</span>
              <span className="score-max">/100</span>
            </div>
          </div>
          {/* score_percentile n'est posé que par l'endpoint du rapport réel ; la démo
              et les rapports relus depuis un partage portent `percentile`. */}
          {(report.score_percentile ?? report.percentile) != null && (
            <div className="score-percentile">
              <span className="score-percentile-main">{t("score_percentile", report.score_percentile ?? report.percentile)}</span>
              <span className="score-percentile-note">
                {report.percentileFromRank === false ? t("score_percentile_unranked") : t("score_percentile_est")}
              </span>
            </div>
          )}
        </div>

        <div className="extra-stats">
          <div className="extra-stat">
            <span className="extra-label">{report.season_winrate != null ? t("winrate_season") : t("winrate")}</span>
            <span className="extra-value" style={{ color: (report.season_winrate ?? report.extra?.winrate ?? 0) >= 50 ? "#00d46a" : "#e05c5c" }}>
              {report.season_winrate != null ? `${report.season_winrate}%` : `${report.extra?.winrate ?? 0}%`}
            </span>
          </div>
          <div className="extra-stat">
            <span className="extra-label">{t("kda_avg")}</span>
            <span className="extra-value">{report.extra?.kda ?? "—"}</span>
          </div>
          {report.extra && (
            <div className="extra-stat">
              <span className="extra-label">{t("kda")}</span>
              <span className="extra-value">{report.extra.kills} / {report.extra.deaths} / {report.extra.assists}</span>
            </div>
          )}
          {report.extra?.dpm ? (
            <div className="extra-stat">
              <span className="extra-label">{t("damage")}</span>
              <span className="extra-value gold">{report.extra.dpm}/min</span>
            </div>
          ) : null}
          {report.extra?.kp ? (
            <div className="extra-stat">
              <span className="extra-label">{t("kill_participation")}</span>
              <span className="extra-value ice">{report.extra.kp}%</span>
            </div>
          ) : null}
          {report.extra && (
            <div className="extra-stat">
              <span className="extra-label">{t("games")}</span>
              <span className="extra-value">{report.extra.matches}</span>
            </div>
          )}
        </div>

        <div className="card axes-card framed">
          <div className="axes-header">
            <span>{t("axes_title")}</span>
            {mainRole && <span className="axes-role">{t(`role_${mainRole}`) || mainRole}</span>}
          </div>
          <div className="axes-list">
            {axes.map((axis) => (
              <div className="axis-row" key={axis.key}>
                <span className="axis-label">{axis.label}</span>
                <span className="axis-track">
                  <span
                    className={`axis-fill axis-fill--${axis.value >= 65 ? "high" : axis.value >= 40 ? "mid" : "low"}`}
                    style={{ transform: `scaleX(${axis.value / 100})` }}
                  />
                </span>
                <span className="axis-value">{axis.value}</span>
              </div>
            ))}
          </div>
          <div className="axes-legend">{t("axes_legend")}</div>
        </div>

        <div className="grid two-col">
          {/* Badge */}
          <div className="card badge-card framed">
            <div
              className="badge-illustration"
              style={isPremium ? { backgroundImage: `url(${champSplash(badgeChamp)})` } : {}}
            >
              {/* Pas d'accroche commerciale sur une carte partagee : le visiteur
                  n'a rien demande, et le cadenas laissait 240 px de vide au milieu
                  de ce qu'on diffuse. Le bandeau dore du badge suffit a la carte. */}
              {!isPremium && !readOnly && (
                <div className="badge-locked-hint">
                  <span className="badge-locked-icon"><Lock size={14} /></span>
                  <span className="badge-locked-text">{t("badge_locked")}</span>
                </div>
              )}
              <div className="badge-burst" />
              <div className="badge-ribbon">
                <span>✦ {report.badge?.toUpperCase() || "DAMAGE MACHINE"} ✦</span>
              </div>
            </div>
            <div className="badge-text">
              <h3 className="badge-title">{report.badge}</h3>
              <p className={`badge-sub${roastLoading ? " badge-sub--loading" : ""}`}>{report.phrase}</p>
              {!readOnly && !isDemoMode && (
                <div className="roast-controls">
                  <div className="roast-levels">
                    {[["gentil", t("roast_gentil")], ["piquant", t("roast_piquant")], ["sans_pitie", t("roast_sans_pitie")]].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className={`roast-level${roastLevel === id ? " roast-level--on" : ""}`}
                        onClick={() => onRoastLevel && onRoastLevel(id)}
                        disabled={roastLoading}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="roast-again" onClick={() => onReroast && onReroast()} disabled={roastLoading}>
                    <Zap size={12} style={{ marginRight: '0.3125rem', verticalAlign: "middle" }} />
                    {roastLoading ? t("roast_loading") : t("roast_again")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Radar */}
          <div className="card radar-card framed">
            <div className="radar-header">{t("radar_title")}</div>
            <div className="radar-wrapper">
              <Radar data={radarData} options={options} />
            </div>
          </div>
        </div>

        {/* Champion performance list */}
        {report.mapPerformance?.length > 0 && (
          <div className="card champ-perf-card framed">
            <div className="champ-perf-header">
              <span className="champ-perf-title">{t("champ_perf_title")}</span>
              <span className="champ-perf-hint">{t("champ_perf_hint")}</span>
            </div>
            <ul className="champ-perf-list">
              {report.mapPerformance.slice(0, 10).map((cp, i) => {
                const games = cp.games ?? 1;
                const lowSample = games < 3;
                const score = cp.score ?? 0;
                return (
                  <li key={i} className={`champ-perf-row${lowSample ? " champ-perf-row--low" : ""}`}>
                    <span className="champ-perf-name">{cp.map}</span>
                    <div className="champ-perf-bar-wrap">
                      <div
                        className="champ-perf-bar-fill"
                        style={{ width: `${score}%`, opacity: lowSample ? 0.45 : 1 }}
                      />
                    </div>
                    <span className="champ-perf-score" style={{ opacity: lowSample ? 0.6 : 1 }}>
                      {lowSample ? "~" : ""}{score.toFixed(0)}
                    </span>
                    <span className="champ-perf-games">
                      {games} {t(games > 1 ? "champ_perf_games" : "champ_perf_game")}
                      {lowSample && <span className="champ-perf-low-badge" title={t("champ_perf_low_tooltip")}>⚠</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
            {report.mapPerformance.length > 10 && (() => {
              const hidden = report.mapPerformance.slice(10);
              const hiddenGames = hidden.reduce((acc, cp) => acc + (cp.games ?? 1), 0);
              return (
                <p className="champ-perf-footer" style={{ color: "var(--muted)", marginTop: '0.25rem' }}>
                  {t("champ_perf_hidden", hidden.length, hiddenGames)}
                </p>
              );
            })()}
            <p className="champ-perf-footer">{t("champ_perf_footer")}</p>
          </div>
        )}

        {/* Conseils d'amélioration */}
        {/* Conseils / Coach IA selon le statut Premium */}
        {isDemoMode ? (
          <div className="card ai-roast-card framed">
            <div className="ai-roast-header">
              <span className="ai-roast-icon"><Bot size={20} /></span>
              <div style={{ flex: 1 }}>
                <div className="ai-roast-title">{lang === "fr" ? "Coach IA personnalisé" : "AI-powered coaching"}</div>
                <div className="ai-roast-sub">{lang === "fr" ? "Analyse tes stats et génère des conseils sur mesure" : "Analyze your stats and get tailored advice"}</div>
              </div>
              <span className="ai-demo-badge">{lang === "fr" ? "Exemple" : "Example"}</span>
            </div>
            <TexteIA className="ai-roast-text">
              {(() => {
                const pseudo = report.player || "joueur";
                const rank = report.rank || "Unranked";
                const stats = report.stats || {};
                const kda = stats.KDA !== undefined ? stats.KDA : "2.1";
                const winrate = stats.Winrate !== undefined ? stats.Winrate : "50";
                const tips = report.tips || [];
                const tip1 = (tips[0]?.title || (lang === "fr" ? "positionnement" : "positioning")).toLowerCase();
                const tip2 = (tips[1]?.title || (lang === "fr" ? "gestion des ressources" : "resource management")).toLowerCase();
                if (lang === "fr") {
                  return `1. ${pseudo}, ton ${tip1} est ton principal point de progression. Avec un KDA de ${kda}, tu t'exposes à des trades défavorables — travaille sur les échanges en sécurité.\n\n2. À ${rank}, ton winrate de ${winrate}% cache une variance importante. Identifie les situations où tu perds le contrôle de la partie et évite de forcer en late game.\n\n3. Ta constance est ton meilleur atout. Concentre-toi sur ${tip2} et tu verras une progression rapide dans les prochaines semaines.`;
                }
                return `1. ${pseudo}, your ${tip1} is your main growth area. With a KDA of ${kda}, you're exposing yourself to unfavorable trades — work on safe exchanges.\n\n2. At ${rank}, your ${winrate}% winrate hides significant variance. Identify when you lose game control and avoid forcing plays in late game.\n\n3. Your consistency is your best asset. Focus on ${tip2} and you'll see rapid improvement over the next few weeks.`;
              })()}
            </TexteIA>
            <p className="ai-demo-note">
              {lang === "fr"
                ? "Ceci est un exemple généré automatiquement — ton analyse IA sera basée sur tes vraies parties."
                : "This is an auto-generated example — your AI analysis will be based on your actual games."}
            </p>
            <button className="cta cta-primary ai-roast-btn" onClick={() => {
              const userIsLoggedIn = !!currentUser;
              const userIsPremium = !!currentUser?.premium;
              if (userIsLoggedIn && userIsPremium) {
                onReset && onReset();
              } else {
                handleUpsell();
              }
            }}>
              {(() => {
                const userIsLoggedIn = !!currentUser;
                const userIsPremium = !!currentUser?.premium;
                if (userIsLoggedIn && userIsPremium) {
                  return lang === "fr" ? "Analyser mon vrai compte →" : "Analyze my account →";
                }
                if (!userIsLoggedIn) {
                  return lang === "fr" ? "Créer un compte gratuit" : "Create a free account";
                }
                return lang === "fr" ? "Passer Premium" : "Go Premium";
              })()}
            </button>
          </div>
        ) : isPremium && !readOnly ? (
          <div className="card ai-roast-card framed">
            <div className="ai-roast-header">
              <span className="ai-roast-icon"><Bot size={20} /></span>
              <div>
                <div className="ai-roast-title">{lang === "fr" ? "Coach IA personnalisé" : "AI-powered coaching"}</div>
                <div className="ai-roast-sub">{lang === "fr" ? "Analyse tes stats et génère des conseils sur mesure" : "Analyze your stats and get tailored advice"}</div>
              </div>
            </div>
            {aiRoast ? (
              <>
                <TexteIA className="ai-roast-text">{aiRoast}</TexteIA>
                <div className="ai-followup-block">
                  <div className="ai-followup-input-row">
                    <input
                      className="ai-followup-input"
                      type="text"
                      maxLength={300}
                      placeholder={t("ai_followup_placeholder")}
                      value={followupQ}
                      onChange={e => setFollowupQ(e.target.value)}
                      onKeyDown={async e => { if (e.key === "Enter" && followupQ.trim()) e.target.blur(); }}
                    />
                    <button
                      className="cta cta-primary ai-followup-btn"
                      disabled={followupLoading || !followupQ.trim()}
                      onClick={async () => {
                        if (!followupQ.trim()) return;
                        setFollowupLoading(true);
                        setFollowupError(null);
                        setFollowupA(null);
                        try {
                          const token = localStorage.getItem("rms_token");
                          const region = report.region || "euw1";
                          const pseudo = report.player;
                          const resp = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL || "https://john24dev-roast-my-stats-backend.hf.space"}/ai-followup/${region}/${encodeURIComponent(pseudo)}`,
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                              body: JSON.stringify({ question: followupQ, lang }),
                            }
                          );
                          if (!resp.ok) {
                            setFollowupError(aiErrorMessage(resp.status));
                            return;
                          }
                          const data = await resp.json();
                          setFollowupA(data.answer);
                        } catch {
                          setFollowupError(aiErrorMessage(null));
                        } finally {
                          setFollowupLoading(false);
                        }
                      }}
                    >
                      {followupLoading ? t("ai_followup_loading") : t("ai_followup_btn")}
                    </button>
                  </div>
                  {followupA && <TexteIA className="ai-followup-answer">{followupA}</TexteIA>}
                  {followupError && <p className="ai-roast-error">{followupError}</p>}
                </div>
              </>
            ) : (
              <button
                className="cta cta-primary ai-roast-btn"
                disabled={aiRoastLoading}
                onClick={async () => {
                  const token = localStorage.getItem("rms_token");
                  if (!token) { handleUpsell(); return; }
                  setAiRoastLoading(true);
                  setAiRoastError(null);
                  try {
                    const region = report.region || "euw1";
                    const pseudo = report.player;
                    const resp = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL || "https://john24dev-roast-my-stats-backend.hf.space"}/ai-roast/${region}/${encodeURIComponent(pseudo)}?lang=${lang}`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!resp.ok) {
                      setAiRoastError(aiErrorMessage(resp.status));
                      if (resp.status === 403) handleUpsell();
                      return;
                    }
                    const data = await resp.json();
                    setAiRoast(data.roast);
                  } catch {
                    setAiRoastError(aiErrorMessage(null));
                  } finally {
                    setAiRoastLoading(false);
                  }
                }}
              >
                {aiRoastLoading ? (lang === "fr" ? "Analyse en cours..." : "Analyzing...") : (lang === "fr" ? "Générer mon analyse" : "Generate my analysis")}
              </button>
            )}
            {aiRoastError && <p className="ai-roast-error">{aiRoastError}</p>}
          </div>
        ) : (
          <>
            {!readOnly && (
              <div className="card ai-roast-card ai-roast-teaser framed">
                <div className="ai-roast-header">
                  <span className="ai-roast-icon"><Bot size={20} /></span>
                  <div>
                    <div className="ai-roast-title">{lang === "fr" ? "Coach IA personnalisé" : "AI-powered coaching"}</div>
                    <div className="ai-roast-sub">{lang === "fr" ? "Analyse tes stats et génère des conseils sur mesure" : "Analyze your stats and get tailored advice"}</div>
                  </div>
                </div>
                <div className="ai-roast-blur-wrapper">
                  <p className="ai-roast-text" style={{ whiteSpace: "pre-line", filter: "blur(5px)", userSelect: "none", pointerEvents: "none" }}>
                    {(() => {
                      const pseudo = report.player || "joueur";
                      const rank = report.rank || "Unranked";
                      const stats = report.stats || {};
                      const kda = stats.KDA !== undefined ? stats.KDA : "2.1";
                      const winrate = stats.Winrate !== undefined ? stats.Winrate : "50";
                      const tips = report.tips || [];
                      const tip1 = (tips[0]?.title || (lang === "fr" ? "positionnement" : "positioning")).toLowerCase();
                      const tip2 = (tips[1]?.title || (lang === "fr" ? "gestion des ressources" : "resource management")).toLowerCase();
                      if (lang === "fr") {
                        return `1. ${pseudo}, ton ${tip1} est ton principal point de progression. Avec un KDA de ${kda}, tu t'exposes à des trades défavorables — travaille sur les échanges en sécurité.\n\n2. À ${rank}, ton winrate de ${winrate}% cache une variance importante. Identifie les situations où tu perds le contrôle de la partie et évite de forcer en late game.\n\n3. Ta constance est ton meilleur atout. Concentre-toi sur ${tip2} et tu verras une progression rapide dans les prochaines semaines.`;
                      }
                      return `1. ${pseudo}, your ${tip1} is your main growth area. With a KDA of ${kda}, you're exposing yourself to unfavorable trades — work on safe exchanges.\n\n2. At ${rank}, your ${winrate}% winrate hides significant variance. Identify when you lose game control and avoid forcing plays in late game.\n\n3. Your consistency is your best asset. Focus on ${tip2} and you'll see rapid improvement over the next few weeks.`;
                    })()}
                  </p>
                  <div className="ai-roast-blur-overlay">
                    <div className="ai-roast-overlay-content">
                      <Crown size={22} style={{ color: "#f59e0b", marginBottom: '0.375rem' }} />
                      <div className="ai-roast-overlay-title">{lang === "fr" ? "Ton analyse complète t'attend" : "Your full analysis is ready"}</div>
                      <div className="ai-roast-overlay-sub">{lang === "fr" ? "Débloque ton coaching IA personnalisé" : "Unlock your personalized AI coaching"}</div>
                      <button className="cta cta-primary ai-roast-overlay-btn" onClick={handleUpsell}>
                        <Crown size={13} style={{ marginRight: '0.375rem', verticalAlign: "middle" }} />
                        {currentUser
                          ? (lang === "fr" ? "Débloquer le Pass 24h" : "Unlock the 24h Pass")
                          : (lang === "fr" ? "Pass 24h offert à l'inscription" : "First 24h Pass free on signup")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {report.tips && report.tips.length > 0 && (() => {
            const INITIAL = 4;
            const visibleTips = tipsExpanded ? report.tips : report.tips.slice(0, INITIAL);
            const hiddenCount = report.tips.length - INITIAL;
            return (
              <div className="card tips-card framed">
                <div className="tips-header">
                  <h3 className="tips-title"><Zap size={18} style={{ verticalAlign: "middle", marginRight: '0.375rem', marginBottom: 2 }} />{t("tips_title")}</h3>
                  <div style={{ display: "flex", alignItems: "center", gap: '0.5rem', flexWrap: "wrap" }}>
                    <span className="tips-sub">{t("tips_based_on", report.extra?.matches || 10)}</span>
                    {!isPremium && !readOnly && (
                      <button className="tips-upgrade-btn" onClick={handleUpsell}>
                        <Crown size={11} style={{ marginRight: '0.25rem', verticalAlign: "middle" }} />{report.tips.length >= 10 ? t("tips_upgrade_50") : t("tips_upgrade_free")}
                      </button>
                    )}
                  </div>
                </div>
                <ul className="tips-list">
                  {visibleTips.map((tip, i) => (
                    <li className="tip-item" key={i}>
                      <span className="tip-icon">{tip.icon}</span>
                      <div className="tip-content">
                        <span className="tip-title">{tip.title}</span>
                        <span className="tip-text">{tip.text}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                {report.tips.length > INITIAL && (
                  <button
                    className="tips-expand-btn"
                    onClick={() => setTipsExpanded(e => !e)}
                  >
                    {tipsExpanded ? t("tips_collapse") : t("tips_show_more", hiddenCount)}
                  </button>
                )}
                {!readOnly && (
                  <div className="tips-premium-upsell">
                    <Bot size={13} style={{ flexShrink: 0, color: "#818cf8" }} />
                    <span>{lang === "fr" ? "Conseils IA personnalisés disponibles en" : "AI-powered coaching available in"}</span>
                    <button className="tips-upgrade-btn" onClick={handleUpsell}><Crown size={11} style={{ marginRight: '0.25rem', verticalAlign: "middle" }} />Premium</button>
                  </div>
                )}
              </div>
            );
          })()}
          </>
        )}

        {/* Teaser premium pour les non-abonnés */}
        {!isPremium && !readOnly && (
          <div className="premium-teaser">
            <div className="premium-teaser-left">
              <span className="premium-teaser-icon"><Crown size={20} /></span>
              <div>
                <div className="premium-teaser-title">{t("premium_teaser_title")}</div>
                <div className="premium-teaser-sub">{t("premium_teaser_sub")}</div>
              </div>
            </div>
            <button className="cta cta-primary premium-teaser-btn" onClick={handleUpsell}>
              {t("premium_teaser_btn")}
            </button>
          </div>
        )}

        {/* Section basse */}
        <div>
          <div className="grid two-col lower">
            <div className={`card friends-card framed${friendsRanking.length > 0 ? " friends-card--ranked" : ""}`}>
              <h3>{t("friends_title")}</h3>
              {friendsRanking.length > 0 ? (
                <ul className="friends-list">
                  {friendsRanking.map((friend, index) => (
                    <li
                      className={`friend${friend.isCurrentPlayer ? " friend-current" : ""}`}
                      key={friend.name}
                    >
                      <span className={`rank rank-${index + 1}`}>{index + 1}.</span>
                      <span className="friend-name">{friend.name}</span>
                      <span className="muted">— {friend.score}/100</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="friends-empty">{t("friends_empty")}</p>
              )}
            </div>

            <div className="card agent-card framed">
              <div
                className="agent-graphic"
                style={{ backgroundImage: `url(${champSplash(secondChamp)})` }}
              />
            </div>
          </div>

          <div className="grid two-col lower">
            <div className="card history-card framed" style={{ position: "relative" }}>
              <h3>{t("history_title")}</h3>
              {estConnecte ? (
                history.length > 0 ? (
                  <>
                    {playerHistory.length >= 2 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: '0.5rem', gap: '0.5rem', flexWrap: "wrap" }}>
                          <p style={{ fontSize: '0.6875rem', color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>
                            {t("history_progression", report.player)}
                          </p>
                          {avgScore !== null && (
                            <span style={{ fontSize: '0.6875rem', color: "var(--muted)" }}>{t("history_avg", avgScore)}</span>
                          )}
                        </div>
                        {/* Les boutons vivent hors du rendu conditionnel du graphique :
                            une periode vide doit rester quittable. */}
                        <div className="periode-tabs">
                          {periodesDispo.map((p) => (
                            <button
                              key={p.cle}
                              type="button"
                              className={`periode-tab${p.cle === periodeActive.cle ? " periode-tab--active" : ""}`}
                              disabled={p.points < 2}
                              onClick={() => setPeriode(p.cle)}
                            >
                              {t(p.libelle)}
                            </button>
                          ))}
                          {libelleAgregat && <span className="periode-info">{libelleAgregat}</span>}
                        </div>
                        {lineChartData
                          ? <Line data={lineChartData} options={lineChartOptions} />
                          : <p className="muted" style={{ fontSize: '0.75rem', textAlign: "center", padding: '1.5rem 0' }}>{t("periode_vide")}</p>}
                      </div>
                    )}
                    {formattedHistory.length === 0 && (
                      <p className="muted" style={{ fontSize: '0.75rem', textAlign: "center", padding: '0.5rem 0' }}>{t("periode_vide")}</p>
                    )}
                    <ul className="history-list">
                      {(historyExpanded ? formattedHistory : formattedHistory.slice(0, HISTORY_PREVIEW)).map((item, i) => (
                        <li className="history-item" key={i}>
                          <span className="history-date"><Play size={11} style={{ verticalAlign: "middle", marginRight: '0.3125rem', opacity: 0.7 }} />{item.date} — {item.player} — {item.score}/100</span>
                          <span className="muted">"{item.phrase}"</span>
                        </li>
                      ))}
                    </ul>
                    {formattedHistory.length > HISTORY_PREVIEW && (
                      <button className="history-toggle-btn" onClick={() => setHistoryExpanded((v) => !v)}>
                        {historyExpanded ? t("history_show_less") : t("history_show_more", formattedHistory.length - HISTORY_PREVIEW)}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="muted" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{t("history_empty")}</p>
                )
              ) : (
                <div style={{ position: "relative" }}>
                  {formattedHistory.length > 0 && (
                    <ul className="history-list" style={{ filter: "blur(4px)", pointerEvents: "none", userSelect: "none", marginBottom: '0.5rem' }}>
                      {formattedHistory.slice(0, 2).map((item, i) => (
                        <li className="history-item" key={i}>
                          <span className="history-date"><Play size={11} style={{ verticalAlign: "middle", marginRight: '0.3125rem', opacity: 0.7 }} />{item.date} — {item.player} — {item.score}/100</span>
                          <span className="muted">"{item.phrase}"</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div style={{ textAlign: "center", padding: formattedHistory.length > 0 ? "12px 0 8px" : "24px 0 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: '0.625rem' }}>
                    <Crown size={28} style={{ opacity: 0.6, color: "var(--gold)" }} />
                    <p className="muted" style={{ fontSize: '0.75rem', margin: 0 }}>
                      {t("history_premium_locked")}
                    </p>
                    <button className="cta cta-primary" style={{ fontSize: '0.75rem' }} onClick={handleUpsell}>
                      {t("history_go_premium")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: '1rem' }}>
            {!readOnly && (
              <div className="card framed" style={{ padding: "0.75rem 1rem", textAlign: "center" }}>
                {accuracyVoted ? (
                  <p style={{ fontSize: '0.8125rem', color: "var(--gold)", margin: 0 }}>{t("accuracy_thanks")}</p>
                ) : (
                  <>
                    <p style={{ fontSize: '0.8125rem', color: "var(--muted)", margin: "0 0 0.625rem" }}>{t("accuracy_question")}</p>
                    <div style={{ display: "flex", gap: '0.625rem', justifyContent: "center" }}>
                      {[{ v: true, label: t("accuracy_yes") }, { v: false, label: t("accuracy_no") }].map(({ v, label }) => (
                        <button
                          key={String(v)}
                          className="cta cta-ghost"
                          style={{ fontSize: '0.875rem', padding: "0.375rem 1.25rem" }}
                          onClick={async () => {
                            // On ne remercie qu'une fois le vote reellement
                            // enregistre : sinon l'avis part a la poubelle et
                            // l'utilisateur croit avoir contribue.
                            setAccuracyError(false);
                            try {
                              await (onAccuracyFeedback && onAccuracyFeedback(v));
                              localStorage.setItem(accuracyKey, "1");
                              setAccuracyVoted(true);
                            } catch {
                              setAccuracyError(true);
                            }
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {accuracyError && (
                      <p style={{ fontSize: '0.75rem', color: "var(--danger, #f44336)", margin: "0.625rem 0 0" }}>
                        {t("accuracy_error")}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="card framed buttons">
              {readOnly ? (
                <div className="readonly-challenge-block">
                  <div className="readonly-challenge-score">
                    <span className="readonly-challenge-number">{report.score}</span>
                    <span className="readonly-challenge-max">/100</span>
                  </div>
                  <div className="readonly-challenge-text">
                    <span className="readonly-challenge-badge">"{report.badge}"</span>
                    <span className="readonly-challenge-sub">{t("readonly_challenge_sub", report.player)}</span>
                  </div>
                  <button className="cta cta-primary wide readonly-challenge-btn" onClick={() => window.open(window.location.origin, "_blank")}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: '0.4375rem' }}
                  >
                    <ArrowRight size={14} />{t("generate_own")}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    className="cta cta-ghost wide"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: '0.4375rem' }}
                  >
                    <ArrowUp size={14} />{t("new_report_btn")}
                  </button>
                  <button className="cta cta-primary wide" onClick={() => onChallenge && onChallenge(report.player)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: '0.4375rem' }}
                  >
                    <Play size={14} />{t("challenge_btn")}
                  </button>
                </>
              )}
              {!readOnly && (
                <>
                  <button className="cta cta-blue wide" onClick={() => onShare && onShare(report)} disabled={shareLoading}>
                    {shareLoading ? "..." : t("share_btn")}
                  </button>
                </>
              )}
            </div>
            </div>{/* fin wrapper colonne droite */}
          </div>
        </div>
        {/* Progression — la preuve chiffrée du « avant / après » */}
        {progression && (
          <div className="progress-section">
            <div className="card framed">
              <h3 className="section-title">{t("prog_title", progression.jours)}</h3>
              <div className="progress-rows">
                {progression.lignes.map((l) => {
                  const delta = l.apres - l.avant;
                  const positif = l.mieux === "haut" ? delta > 0 : delta < 0;
                  const arrondi = (v) => (Number.isInteger(v) ? v : Math.round(v * 10) / 10);
                  return (
                    <div key={l.cle} className="progress-row">
                      <span className="progress-label">{l.label}</span>
                      <span className="progress-values">
                        <span className="progress-before">{arrondi(l.avant)}</span>
                        <ArrowRight size={12} />
                        <span className={`progress-after${positif ? " is-up" : " is-down"}`}>
                          {arrondi(l.apres)}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Trophées */}
        {trophies && trophies.trophies && (
          <div className="trophies-section">
            <div className="card framed">
              <div className="section-header-row">
                <h3 className="section-title">{t("trophies_title")}</h3>
                <span className="trophies-count">{trophies.unlocked}/{trophies.total}</span>
              </div>
              {newTrophies.length > 0 && (
                <div className="trophy-new-banner">
                  {t("trophies_new", newTrophies.map((x) => x.nom).join(", "))}
                </div>
              )}
              <div className="trophy-grid">
                {trophies.trophies.map((tr) => (
                  <div
                    key={tr.code}
                    className={`trophy${tr.unlocked ? " trophy--on" : ""} trophy--${tr.famille}`}
                    title={tr.desc}
                  >
                    <div className="trophy-icon">
                      {tr.unlocked ? <Trophy size={15} /> : <Lock size={13} />}
                    </div>
                    <div className="trophy-body">
                      <span className="trophy-name">{tr.nom}</span>
                      <span className="trophy-desc">{tr.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Communaute — masquee sur une carte partagee : le visiteur n'a pas de
            compte a y publier, et le bloc floute ne lui disait rien d'utile. */}
        {!readOnly && (
        <div className="community-section">
          <div className="card framed community-card" style={{ position: "relative", overflow: "hidden" }}>
            {!estConnecte && (
              <div className="premium-blur-overlay">
                <Crown size={32} style={{ color: "var(--gold)", opacity: 0.8 }} />
                <p>{t("community_premium_locked")}</p>
                <button className="cta cta-primary" onClick={handleUpsell}>{t("history_go_premium")}</button>
              </div>
            )}
            <div style={{ filter: estConnecte ? "none" : "blur(6px)", pointerEvents: estConnecte ? "auto" : "none" }}>
              <div className="leaderboard-header">
                <div>
                  <h3 className="leaderboard-title">{t("community_title")}</h3>
                  <span className="leaderboard-sub">{t("community_sub")}</span>
                </div>
                {isPremium && (
                  <button
                    className={`cta ${isPublic ? "cta-ghost" : "cta-primary"}`}
                    style={{ fontSize: '0.6875rem', padding: "0.3125rem 0.75rem" }}
                    onClick={onTogglePublish}
                  >
                    {isPublic ? t("community_unpublish") : t("community_publish")}
                  </button>
                )}
              </div>
              {community.length > 0 ? (
                <ul className="community-list">
                  {community.map((m, i) => (
                    <li key={i} className={`community-item${m.profile && estConnecte ? " community-item--clickable" : ""}`}
                      onClick={() => m.profile && estConnecte && onViewProfile && onViewProfile(m.profile)}>
                      <div className="community-avatar avatar--premium">{(m.firstname || "?").charAt(0).toUpperCase()}</div>
                      <div className="community-info">
                        <span className="community-name">{m.firstname}</span>
                        <span className="community-player">{m.player}</span>
                      </div>
                      {m.badge && <span className="community-badge">{m.badge}</span>}
                      <span className="community-score">{m.score}<span>/100</span></span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{t("community_empty")}</p>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Classement Global — public */}
        <div className="leaderboard-section" style={{ marginTop: '0.875rem', position: "relative" }}>
          <div className="card framed leaderboard-card">
            <div className="leaderboard-header">
              <h3 className="leaderboard-title">{t("leaderboard_global")}</h3>
              <span className="leaderboard-sub">{t("leaderboard_best_score")}</span>
            </div>

            {leaderboard.length > 0 ? (
              <>
                {/* Podium top 3 */}
                <div className="lb-podium">
                  {[leaderboard[1], leaderboard[0], leaderboard[2]].filter(Boolean).map((entry) => {
                    const podiumOrder = entry.rank === 1 ? 1 : entry.rank === 2 ? 0 : 2;
                    const isMe = entry.player === report.player;
                    return (
                      <div
                        key={entry.rank}
                        className={`lb-podium-slot lb-podium-${entry.rank}${isMe ? " lb-me" : ""}`}
                        style={{ order: podiumOrder }}
                      >
                        <div className="lb-podium-crown">
                          {entry.rank === 1 ? <Trophy size={16} style={{ color: "var(--gold)" }} /> : entry.rank === 2 ? "2e" : "3e"}
                        </div>
                        <div className="lb-podium-avatar">
                          {entry.player.charAt(0).toUpperCase()}
                        </div>
                        <div className="lb-podium-name">{entry.player}</div>
                        {entry.badge && <div className="lb-podium-badge">{entry.badge}</div>}
                        <div className="lb-podium-score">{entry.score}<span>/100</span></div>
                        <div className="lb-podium-bar" />
                      </div>
                    );
                  })}
                </div>

                {/* Reste du classement */}
                {leaderboard.length > 3 && (
                  <ol className="lb-list">
                    {leaderboard.slice(3).map((entry) => {
                      const isMe = entry.player === report.player;
                      return (
                        <li key={entry.rank} className={`lb-row${isMe ? " lb-me" : ""}`}>
                          <span className="lb-row-rank">#{entry.rank}</span>
                          <span className="lb-row-avatar">{entry.player.charAt(0).toUpperCase()}</span>
                          <span className="lb-row-player">{entry.player}</span>
                          {entry.badge && <span className="lb-row-badge">{entry.badge}</span>}
                          <span className="lb-row-score">{entry.score}/100</span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </>
            ) : (
              <p className="muted" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                {t("leaderboard_global_empty")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardVisual;
